/**
 * La pestaña vertical fija del borde derecho y el estado abierto/cerrado del panel.
 *
 * `closed` no es un estado de React: es el panel DESMONTADO. Lo que sobrevive al cierre (borrador,
 * audio, capturas) vive en `FeedbackService`, no acá.
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {SupportChatService} from '../support_chat/SupportChatService';
import FeedbackService from './FeedbackService';
import {FeedbackPanel} from './FeedbackPanel';

export const FeedbackLauncher = ({sessionKey, uiVersion, experienceTier}) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [initialType, setInitialType] = useState(null);
    const launcherRef = useRef(null);

    const closePanel = useCallback(() => {
        setOpen(false);
        // El foco vuelve SIEMPRE a la pestaña, por cualquier vía de cierre (Escape, X, envío).
        // Sin esto, quien navega por teclado queda con el foco en el body y tiene que tabular
        // desde el principio de la página.
        if (launcherRef.current) launcherRef.current.focus();
    }, []);

    const openPanel = useCallback((type = null) => {
        // Dos drawers de 380px en la misma esquina es un bug visual garantizado: si el chat de
        // soporte está abierto, se cierra.
        try {
            if (SupportChatService && typeof SupportChatService.closePanel === 'function') {
                SupportChatService.closePanel();
            }
        } catch (_) {
            /* el chat de soporte es opcional: puede no estar habilitado para esta agencia */
        }
        setInitialType(type);
        setOpen(true);
    }, []);

    // Permite abrir el widget desde cualquier otro punto de la app (`FeedbackService.open()`).
    useEffect(() => FeedbackService.subscribe((event) => {
        if (event.kind === 'open') openPanel(event.type);
        if (event.kind === 'close') closePanel();
    }), [openPanel, closePanel]);

    return (
        <>
            <button
                ref={launcherRef}
                type="button"
                className="feedback-launcher"
                onClick={() => openPanel()}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-controls="dashbo-feedback-panel"
            >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M21 12a8 8 0 0 1-8 8H5l2-3a8 8 0 1 1 14-5z"/>
                </svg>
                {/* El texto va como contenido y no sólo en `aria-label`: `writing-mode` es puramente
                    visual y no afecta al árbol de accesibilidad, así que un lector de pantalla lee
                    esto igual que cualquier botón. */}
                <span className="feedback-launcher__label">{t('feedback.launcher')}</span>
            </button>

            {open && (
                <FeedbackPanel
                    initialType={initialType}
                    sessionKey={sessionKey}
                    uiVersion={uiVersion}
                    experienceTier={experienceTier}
                    onClose={closePanel}
                />
            )}
        </>
    );
};

export default FeedbackLauncher;
